import { FormControl, Grid, Radio, RadioGroup } from '@mui/material';
import { Fragment, memo, useCallback, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { FormControlLabel } from '@mui/material';
import Foreign from '../../shared/foreign';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import Italian from '../../shared/italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/lab';

interface AuthorityProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  dateFormationAct?: string;
  nothingRequired?: boolean;
}

const AuthorityComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  configuration,
  dateFormationAct,
  nothingRequired = false,
}: AuthorityProps): React.ReactElement => {
  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChanges({
        ...(values || {}),
        foreign: {
          ...(values || {}).foreign,
          [key]: value,
        },
      });
    },
    [values, onChanges]
  );

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsItalianChange = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChanges({
        ...(values || {}),
        foreign: {
          ...(valuesNew || {}),
        },
      });
    },
    [values, onChange]
  );

  const isTrasDatiAu: boolean = useMemo(() => {
    return configuration === ConfigurationType.TRASDATIAU;
  }, [configuration]);

  const isTrasDatiA: boolean = useMemo(() => {
    return configuration === ConfigurationType.TRASDATIAF;
  }, [configuration]);

  const isTraeDatiAu: boolean = useMemo(() => {
    return configuration === ConfigurationType.TRAEDATIAU;
  }, [configuration]);

  const isEnteDichaC: boolean = useMemo(() => {
    return configuration === ConfigurationType.ENTEDICHAC;
  }, [configuration]);

  const isAumitita: boolean = useMemo(() => {
    return configuration === ConfigurationType.AUMITITA;
  }, [configuration]);

  return (
    <Grid container spacing={2} item>
      {isEnteDichaC ? (
        <Fragment>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data formazione documento *"
              aria-label="Data formazione documento"
              value={values?.documentDateFormation || null}
              onChange={(date) => {
                onChange('documentDateFormation', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="documentDateFormation"
                  fullWidth
                  helperText={errors?.documentDateFormation}
                  error={touched?.documentDateFormation && Boolean(errors?.documentDateFormation)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Estremi documento *"
              aria-label="Estremi documento"
              id="documentExtremes"
              name="documentExtremes"
              fullWidth
              disabled={disabled}
              helperText={errors?.documentExtremes}
              error={touched?.documentExtremes && Boolean(errors?.documentExtremes)}
              type="text"
              variant="outlined"
              multiline
              value={values?.documentExtremes || ''}
              onChange={(event) => {
                onChange('documentExtremes', event.target.value);
              }}
            />
          </Grid>
        </Fragment>
      ) : undefined}
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Nome ente *"
          aria-label="Nome ente"
          id="nameInstitution"
          name="nameInstitution"
          fullWidth
          disabled={disabled}
          helperText={errors?.nameInstitution}
          error={touched?.nameInstitution && Boolean(errors?.nameInstitution)}
          type="text"
          variant="outlined"
          value={values?.nameInstitution || ''}
          onChange={(event) => {
            onChange('nameInstitution', event.target.value);
          }}
        />
      </Grid>
      {isTrasDatiAu || isTrasDatiA || isEnteDichaC ? (
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Nascita"
              id="isForeignBorn"
              value={values?.isForeign || 'false'}
              onChange={() => {
                onChanges({
                  isForeign: !values?.isForeign,
                  italian: {
                    location: '',
                    province: '',
                    municipality: null,
                  },
                  foreign: {
                    state: null,
                    province: '',
                    municipality: null,
                  },
                });
              }}
            >
              <FormControlLabel
                value="false"
                control={<Radio />}
                label="Italia"
                disabled={disabled}
              />
              <FormControlLabel
                value="true"
                control={<Radio />}
                label="Estero"
                disabled={disabled}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      ) : undefined}
      {isTraeDatiAu || values?.isForeign ? (
        <Foreign
          disabledComponent={disabled}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          onChanges={onFieldsForeignChange}
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          labelState="Stato ente"
          required={!nothingRequired}
          isWithConsulate
          isNotRequiredConsulate={isTrasDatiA}
        />
      ) : (
        <Italian
          disabledComponent={disabled}
          values={values?.italian}
          onChange={onFieldItalianChange}
          onChanges={onFieldsItalianChange}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          fromDate={dateFormationAct ? DateTime.fromISO(dateFormationAct) : null}
          labelMunicipality="Comune"
          labelProvince="Provincia"
          required={!isAumitita}
        />
      )}
    </Grid>
  );
};

AuthorityComponent.displayName = 'Form Authority component';

const Authority = memo(AuthorityComponent);

export default Authority;
